import React, { useRef, useState, useEffect } from "react";
import Slider from "react-slick";
import { FaTrashAlt } from "react-icons/fa";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Cateringgallery = () => {
    const [images, setImages] = useState([]);
    const [aboutUsId, setAboutUsId] = useState(null);
    const [vendorId, setVendorId] = useState(null);
    const fileInputRef = useRef(null);

    // Get vendorId on mount
    useEffect(() => {
        const storedVendorId = localStorage.getItem("vendorId");
        setVendorId(storedVendorId);
    }, []);

    // Fetch existing About Us images
    useEffect(() => {
        if (vendorId) fetchAboutUsData(vendorId);
    }, [vendorId]);

    const fetchAboutUsData = async (vId) => {
        const token = localStorage.getItem("authToken");
        if (!token) return alert("Authorization token missing.");

        try {
            const response = await fetch(
                `https://backend.maamaas.com/catering/api/aboutus/${vId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) return;

            const data = await response.json();
            const galleryData = Array.isArray(data) ? data[0] : data;
            setAboutUsId(galleryData.aboutUsId || null);

            // Map backend image URLs
            const backendImages = [];
            ["image", "image1", "image2", "image3", "image4"].forEach((field) => {
                if (galleryData[field]) {
                    backendImages.push({
                        id: field,
                        src: galleryData[field],
                        isNew: false,
                    });
                }
            });

            setImages(backendImages); // ✅ Show gallery from backend URLs
        } catch (err) {
            console.error("Error fetching About Us:", err);
        }
    };


    const settings = {
        dots: false,
        infinite: images.length > 2,
        speed: 500,
        slidesToShow: Math.min(images.length, 3),
        slidesToScroll: 1,
        autoplay: images.length > 0,
        autoplaySpeed: 2000,
        pauseOnHover: true,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: Math.min(images.length, 2), infinite: images.length > 1 } },
            { breakpoint: 768, settings: { slidesToShow: Math.min(images.length, 1), infinite: images.length > 0 } },
        ],
    };

    const handleAddImageClick = () => {
        if (!vendorId) return alert("User not identified. Please log in.");
        if (images.length >= 4) return alert("Maximum 4 images allowed.");
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        if (!aboutUsId || !vendorId) return;
        const files = Array.from(event.target.files);
        const newImages = files.map(file => ({ file, isNew: true, src: URL.createObjectURL(file) }));
        const updatedImages = [...images, ...newImages];
        if (updatedImages.length > 4) {
            alert("Maximum 4 images allowed.");
            return;
        }
        setImages(updatedImages);

        // Send images as multipart/form-data
        await sendImagesToAPI(updatedImages);
    };

    const sendImagesToAPI = async (currentImagesArray) => {
        const token = localStorage.getItem("authToken");
        if (!token) return alert("Authorization token missing.");
        if (!aboutUsId) return alert("About Us ID missing.");

        try {
            const formData = new FormData();

            // Add the aboutUsData as JSON blob
            const aboutUsBlob = new Blob([JSON.stringify({ aboutUsId })], { type: "application/json" });
            formData.append("aboutUsData", aboutUsBlob);

            // Append images
            currentImagesArray.forEach((img, index) => {
                if (img.file) formData.append(`image${index + 1}`, img.file);
            });

            const response = await fetch(
                `https://backend.maamaas.com/catering/api/vendor/aboutus/update/${aboutUsId}`,
                {
                    method: "PUT",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                }
            );

            // console.log("HTTP status:", response.status);
            const responseText = await response.text();
            // console.log("Response body:", responseText);

            if (!response.ok) {
                alert(`Update failed with status ${response.status}. Check console for details.`);
                return;
            }

            // Refresh gallery after successful upload
            fetchAboutUsData(vendorId);
            alert("Images updated successfully!");
        } catch (err) {
            console.error("Error while updating:", err);
            alert("Error while updating. Check console for details.");
        }
    };

    const handleDelete = (index) => {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);
        if (aboutUsId && vendorId) sendImagesToAPI(updatedImages);
    };

    const ImageCard = ({ src, index }) => (
        <div className="image-card-container">
            <img src={src} alt={`gallery-item-${index}`} className="gallery-image" />
            <button onClick={(e) => { e.stopPropagation(); handleDelete(index); }} className="delete-button">
                <FaTrashAlt size={14} color="red" />
            </button>
        </div>
    );

    return (
        <section className="gallery-section">
            <div className="gallery-header">
                <h1 className="gallery-title">
                    <span className="title-red">Gall</span>
                    <span className="title-black">ery</span>
                </h1>

                <button onClick={handleAddImageClick} disabled={images.length >= 4 || !vendorId} className="add-button">
                    Add+
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                    multiple
                />
            </div>

            {images.length > 0 ? (
                <Slider key={images.length} {...settings}>
                    {images.map((img, idx) => (
                        <div key={idx} className="slider-item">
                            <ImageCard src={img.src} index={idx} />
                        </div>
                    ))}
                </Slider>
            ) : (
                <p className="empty-message">No gallery images uploaded yet. Click "Add+" to upload.</p>
            )}

            <style jsx>{`
                .gallery-section { width: 100%; max-width: 1200px; margin: 0 auto; padding: 20px 15px; }
                .gallery-header { display: flex; align-items: center; margin-bottom: 20px; justify-content: center; flex-wrap: wrap; gap: 15px; }
                .gallery-title { font-size: 38px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; text-shadow: 0 4px 6px rgba(0,0,0,0.1); margin: 0; text-align: center; }
                .title-red { color: red; }
                .title-black { color: black; }
                .add-button { height: 35px; border-radius: 5px; border: none; background: #b15cd6; color: #fff; font-weight: bold; cursor: pointer; width: 80px; transition: all 0.3s ease; }
                .add-button:disabled { background: #ccc; cursor: not-allowed; }
                .file-input { display: none; }
                .image-card-container { position: relative; width: 100%; height: 300px; overflow: hidden; border-radius: 16px; margin: 0 10px; }
                .gallery-image { width: 100%; height: 100%; object-fit: cover; }
                .delete-button { position: absolute; top: 10px; right: 10px; background: rgba(0,0,0,0.6); border: none; border-radius: 50%; padding: 6px; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 2; }
                .empty-message { text-align: center; color: #777; font-style: italic; padding: 20px; }
            `}</style>
        </section>
    );
};

export default Cateringgallery;
